import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
	faAngleDoubleDown,
	faAngleDoubleLeft,
	faAngleDoubleRight,
	faAngleDoubleUp,
	faAngleDown,
	faAngleLeft,
	faAngleRight,
	faAngleUp,
	faCompressArrowsAlt,
	faExpandArrowsAlt,
	faRedo,
	faUndo
} from "@fortawesome/free-solid-svg-icons";

import ButtonIcon from "./ButtonIcon";

import styles from "./ElementPositionEditor.module.css";

type ElementPositionEditorType = {
	element: ContainerElementRef;
	onElementMove: (axis: AxisEnum, value: number) => void;
	onElementRotate: (axis: RotationEnum, value: 1 | -1) => void;
};

type actionType = (event: React.MouseEvent<HTMLDivElement>) => void;

// Note: for rotation, we should check the camera position to adjust of the rotation direction...
const ElementPositionEditor: React.FC<ElementPositionEditorType> = ({
	element,
	onElementMove,
	onElementRotate
}) => {
	const moveElt: actionType = event => {
		const { axis, step: stepStr = "NaN" } = event.currentTarget.dataset;
		const step = Number.parseFloat(stepStr);

		if (!Number.isNaN(step) && (axis === "x" || axis === "y" || axis === "z")) {
			onElementMove(axis, step);
		}
	};
	const rotateElt: actionType = event => {
		const { axis, dir } = event.currentTarget.dataset;

		if (axis === "rotX" || axis === "rotY" || axis === "rotZ") {
			onElementRotate(axis, dir === "-1" ? -1 : 1);
		}
	};
	return (
		<div className={styles.editor}>
			<div className={styles.title}>Position</div>
			<div className={styles.action}>
				<div className={styles.label}>X ({element.position.position?.[0] || 0})</div>
				<Action axis="x" step={-0.1} icon={faAngleDoubleLeft} onClick={moveElt} />
				<Action axis="x" step={-0.01} icon={faAngleLeft} onClick={moveElt} />
				<Action axis="x" step={0.01} icon={faAngleRight} onClick={moveElt} />
				<Action axis="x" step={0.1} icon={faAngleDoubleRight} onClick={moveElt} />
			</div>
			<div className={styles.action}>
				<div className={styles.label}>Y ({element.position.position?.[1] || 0})</div>
				<Action axis="y" step={-0.1} icon={faAngleDoubleDown} onClick={moveElt} />
				<Action axis="y" step={-0.01} icon={faAngleDown} onClick={moveElt} />
				<Action axis="y" step={0.01} icon={faAngleUp} onClick={moveElt} />
				<Action axis="y" step={0.1} icon={faAngleDoubleUp} onClick={moveElt} />
			</div>
			<div className={styles.action}>
				<div className={styles.label}>Z ({element.position.position?.[2] || 0})</div>
				<Action axis="z" step={-0.1} icon={faCompressArrowsAlt} onClick={moveElt} />
				<Action axis="z" step={-0.01} small icon={faCompressArrowsAlt} onClick={moveElt} />
				<Action axis="z" step={0.01} small icon={faExpandArrowsAlt} onClick={moveElt} />
				<Action axis="z" step={0.1} icon={faExpandArrowsAlt} onClick={moveElt} />
			</div>
			<div className={styles.title}>Rotation</div>
			<div className={styles.action}>
				<div className={styles.label}>X ({(element.position.rotX || 0) * 90})</div>
				<ButtonIcon icon={faUndo} data-axis="rotX" data-dir="1" onClick={rotateElt} />
				<ButtonIcon icon={faRedo} data-axis="rotX" data-dir="-1" onClick={rotateElt} />
			</div>
			<div className={styles.action}>
				<div className={styles.label}>Y ({(element.position.rotY || 0) * 90})</div>
				<ButtonIcon icon={faUndo} data-axis="rotY" data-dir="1" onClick={rotateElt} />
				<ButtonIcon icon={faRedo} data-axis="rotY" data-dir="-1" onClick={rotateElt} />
			</div>
			<div className={styles.action}>
				<div className={styles.label}>Z ({(element.position.rotZ || 0) * 90})</div>
				<ButtonIcon icon={faUndo} data-axis="rotZ" data-dir="1" onClick={rotateElt} />
				<ButtonIcon icon={faRedo} data-axis="rotZ" data-dir="-1" onClick={rotateElt} />
			</div>
		</div>
	);
};

const Action: React.FC<{
	axis: AxisEnum;
	step: number;
	icon: IconProp;
	small?: boolean;
	onClick: actionType;
}> = ({ axis, step, icon, small, onClick }) => (
	<ButtonIcon
		icon={icon}
		size={small ? "xs" : undefined}
		data-axis={axis}
		data-step={step}
		onClick={onClick}
	/>
);

export default ElementPositionEditor;
